import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { APP_CONSTANTS, ROUTE_NAMES } from '../constants';
import './LandingNavbar.css';

interface LandingNavbarProps {
  onGetStarted: () => void;
}

const LandingNavbar: React.FC<LandingNavbarProps> = ({ onGetStarted }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update selected section based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === ROUTE_NAMES.home) {
      setSelectedSection('home');
    } else if (path === ROUTE_NAMES.features) {
      setSelectedSection('features');
    } else if (path === ROUTE_NAMES.howItWorks) {
      setSelectedSection('how-it-works');
    } else if (path === ROUTE_NAMES.services) {
      setSelectedSection('services');
    } else if (path === ROUTE_NAMES.commercialServices) {
      setSelectedSection('commercial-services');
    }
  }, [location.pathname]);

  const handleNavClick = (route: string) => {
    setSelectedSection(route);
    setIsMobileMenuOpen(false);
    navigate(route);
  };

  const isMobile = window.innerWidth < 768;
  const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
  const showFullNav = window.innerWidth >= 1024;

  return (
    <motion.nav
      className={`landing-navbar ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="navbar-content">
        <motion.div 
          className="navbar-brand"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => navigate(ROUTE_NAMES.home)}
          style={{ cursor: 'pointer' }}
        >
          <img
            src="/logo.jpg"
            alt="TaskWombat Logo"
            className="brand-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              const fallback = document.createElement('div');
              fallback.className = 'brand-logo-fallback';
              fallback.innerHTML = '<span class="material-icons">task_alt</span>';
              (e.target as HTMLElement).parentElement?.appendChild(fallback);
            }}
          />
          {!isMobile && <span className="brand-title">TaskWombat</span>}
        </motion.div>

        {showFullNav && (
          <div className="nav-links">
            <NavLink
              label="Home"
              isSelected={selectedSection === 'home'}
              onClick={() => handleNavClick(ROUTE_NAMES.home)}
            />
            <NavLink
              label="Features"
              isSelected={selectedSection === 'features'}
              onClick={() => handleNavClick(ROUTE_NAMES.features)}
            />
            <NavLink
              label="How It Works"
              isSelected={selectedSection === 'how-it-works'}
              onClick={() => handleNavClick(ROUTE_NAMES.howItWorks)}
            />
            <NavLink
              label="Services"
              isSelected={selectedSection === 'services'}
              onClick={() => handleNavClick(ROUTE_NAMES.services)}
            />
            <NavLink
              label="Commercial"
              isSelected={selectedSection === 'commercial-services'}
              onClick={() => handleNavClick(ROUTE_NAMES.commercialServices)}
            />
            {!APP_CONSTANTS.isMVPMode && (
                <NavLink
                  label="Testimonials"
                  isSelected={selectedSection === 'testimonials'}
                onClick={() => handleNavClick(ROUTE_NAMES.home)}
                />
            )}
          </div>
        )}

        <div className="nav-actions">
          <motion.button
            className="btn-login"
            onClick={() => navigate(ROUTE_NAMES.login)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isMobile ? (
              <span className="material-icons">person</span>
            ) : (
              <>
                <span className="material-icons" style={{ fontSize: '18px', marginRight: '6px' }}>person</span>
                Login
              </>
            )}
          </motion.button>
          <motion.button
            className="btn-primary"
            onClick={onGetStarted}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isMobile ? 'Get Started' : 'Get Started Today'}
          </motion.button>

          {(isMobile || isTablet) && (
            <button
              className="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className="material-icons">
                {isMobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          )}
        </div>
      </div>

      {isMobileMenuOpen && (isMobile || isTablet) && (
        <motion.div
          className="mobile-menu"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <button onClick={() => handleNavClick(ROUTE_NAMES.home)}>Home</button>
          <button onClick={() => handleNavClick(ROUTE_NAMES.features)}>Features</button>
          <button onClick={() => handleNavClick(ROUTE_NAMES.howItWorks)}>How It Works</button>
          <button onClick={() => handleNavClick(ROUTE_NAMES.services)}>Services</button>
          <button onClick={() => handleNavClick(ROUTE_NAMES.commercialServices)}>Commercial</button>
          {!APP_CONSTANTS.isMVPMode && (
            <button onClick={() => handleNavClick(ROUTE_NAMES.home)}>Testimonials</button>
          )}
          <button onClick={() => handleNavClick(ROUTE_NAMES.login)}>Login</button>
        </motion.div>
      )}
    </motion.nav>
  );
};

interface NavLinkProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

const NavLink: React.FC<NavLinkProps> = ({ label, isSelected, onClick }) => (
  <button
    className={`nav-link ${isSelected ? 'selected' : ''}`}
    onClick={onClick}
  >
    {label}
  </button>
);

export default LandingNavbar;
